/**
 * Converts the flat postAuthnConfigs shape (formerly serialized as data-* attributes
 * for AOSW) into the typed props <CommonLoginIdentityApp /> expects (ADR-002 in
 * common-login-identity-spa's design.md).
 *
 * postAuthnConfigs.postAuthnRoutes is a JSON string produced by
 * authorize-utils.js#enrichApiResponse, shaped as:
 *   [{route:'account/sanitize'}, {route:'workflow'}, {url: '<final okta authorize url>'}]
 * common-login-identity-spa's PostAuthnStep shape is:
 *   {needsSanitize:true} | {workflow:true} | {authorizeToOkta:true, url}
 *
 * PKCE/OIDC fields (nonce, codeChallenge, codeChallengeMethod) are not exposed as
 * discrete fields on postAuthnConfigs today — they're only present inside the
 * already-assembled authorize URL, so they're parsed back out of it here.
 *
 * hostInputs carries values the new SPA needs that AOSW derives on its own
 * (cookies, the page's authorize-params script tag, the URL hash, parsedPayload).
 * They are deliberately kept off postAuthnConfigs so the AOSW path's props stay
 * exactly as they were. See CommonLoginIdentityWidget.js for where each is sourced.
 */
export function toCommonLoginProps(postAuthnConfigs, hostInputs) {
  const {
    oktaAuth,
    emailToken,
    lwaUniqId,
    initialRoute,
    tAndCShown,
    postAuthorizeParams,
  } = hostInputs;
  const rawRoutes = JSON.parse(postAuthnConfigs.postAuthnRoutes || '[]');

  const postAuthnRoutes = rawRoutes
    .map((step) => {
      if (step.route === 'account/sanitize') return { needsSanitize: true };
      if (step.route === 'workflow') return { workflow: true };
      if (step.url) return { authorizeToOkta: true, url: step.url };
      return null;
    })
    .filter(Boolean);

  const authorizeToOktaStep = rawRoutes.find((step) => step.url);
  const originalAuthorizeUrl = authorizeToOktaStep ? authorizeToOktaStep.url : '';

  let authorizeUrlParams = {};
  try {
    authorizeUrlParams = Object.fromEntries(new URL(originalAuthorizeUrl).searchParams);
  } catch (e) {
    // originalAuthorizeUrl missing or malformed — fall through with empty params
  }

  const originalAuthorizePostParams =
    parseAuthorizePostParams(postAuthnConfigs.authorizePostParams) || postAuthorizeParams;

  const responseMode = originalAuthorizePostParams
    ? 'form_post'
    : authorizeUrlParams.response_mode || 'query';

  let i18n;
  try {
    i18n = postAuthnConfigs.i18n ? JSON.parse(postAuthnConfigs.i18n) : undefined;
  } catch (e) {
    i18n = undefined;
  }

  return {
    workflow: postAuthnConfigs.workflow,
    appType: postAuthnConfigs.appType,
    iamCoreUrl: postAuthnConfigs.iamCoreUrl,
    // LWA owns the Unified Patient Login branding gate, just as it does for AOSW.
    // Common SPA only renders the already-selected logo.
    logoUrl: postAuthnConfigs.logoUrl,
    logoText: postAuthnConfigs.logoText,
    authorizeContext: {
      clientId: postAuthnConfigs.clientId || authorizeUrlParams.client_id || '',
      redirectUri: postAuthnConfigs.redirectUri || authorizeUrlParams.redirect_uri || '',
      scope: postAuthnConfigs.scope || authorizeUrlParams.scope || '',
      state: postAuthnConfigs.state || authorizeUrlParams.state || '',
      nonce: authorizeUrlParams.nonce || '',
      codeChallenge: authorizeUrlParams.code_challenge || '',
      codeChallengeMethod: authorizeUrlParams.code_challenge_method || 'S256',
      responseMode,
      originalAuthorizeUrl,
      originalAuthorizePostParams,
    },
    preAuthnConfig: {
      username: postAuthnConfigs.username,
      idp: postAuthnConfigs.idp,
      workflowHandlerEndpoint: postAuthnConfigs.workflowHandlerEndpoint,
      displayName: postAuthnConfigs.displayName,
      appType: postAuthnConfigs.appType,
      workflowType: postAuthnConfigs.workflowType,
      i18n,
      language: postAuthnConfigs.language,
      signupLink: postAuthnConfigs.signupLink,
      callProcessCreds: postAuthnConfigs.callProcessCreds,
      updateUserMfaProfile: postAuthnConfigs.updateUserMfaProfile,
      ohswBaseUrl: postAuthnConfigs.oktaUrl,
      // Required by cppi's workflow-handler on every call to resolve anetInstance
      // (workflow-handler.js) — iam-core never returns this in its config
      // response, so it's reconstructed from the /authorize query string upstream
      // in authorize-utils.js#enrichApiResponse and threaded through here.
      aud: postAuthnConfigs.aud,
      // AOSW's WorkflowHandlerUtil.js sends these on every workflow-handler call;
      // without them cppi never sees a registration email-invite token and treats
      // every brand-new registrant as a generic error case.
      emailToken,
      lwaUniqId,
      skipPatientSummary: postAuthnConfigs.skipPatientSummary,
    },
    postAuthnRoutes,
    initialRoute,
    sanitizeInfo: postAuthnConfigs.sanitizeInfo,
    tAndCShown,
    userState: postAuthnConfigs.userState,
    i18n,
    amplitudeApiKey: postAuthnConfigs.amplitudeApiKey,
    oktaAuth,
    accessToken: postAuthnConfigs.accessToken,
    idToken: postAuthnConfigs.idToken,
  };
}

/**
 * authorizePostParams is a JSON string (PreAuthorize.js builds it via
 * JSON.stringify(authorizePostParamsObject), matching AOSW's string-typed
 * data-authorize-post-params DOM attribute). Returns the parsed object, or
 * undefined when absent/empty/malformed so the replay falls back to GET.
 */
function parseAuthorizePostParams(authorizePostParams) {
  try {
    const parsed = JSON.parse(authorizePostParams || '{}');
    return Object.keys(parsed).length > 0 ? parsed : undefined;
  } catch (e) {
    return undefined;
  }
}
